import { formatKMBWithPlaceholder } from '@intelligence/v3/utils/number';
import { renderBarChartTooltip } from '../../Tooltip';
import {
  BAR_CHART_COLORS,
  BAR_WIDTH,
  BAR_MAX_WIDTH,
  BAR_MIN_WIDTH,
  HIGHLIGHT_BG_COLOR,
  BAR_GRID_WITH_LABEL,
  BAR_GRID_WITHOUT_LABEL,
} from '../constants';

/**
 * 生成 series 数据
 * @param data 图表数据
 * @param showLabel 是否显示柱子顶部标签
 * @param valueLabelFormatter 数值标签格式化函数
 * @returns series 数据数组
 */
const getBarChartSeriesData = (
  data: IBarChartData[],
  showLabel?: boolean,
  valueLabelFormatter?: TValueFormatter | null,
): any[] => {
  const formatter = valueLabelFormatter || formatKMBWithPlaceholder;

  return data.map((item, index) => ({
    type: 'bar',
    name: item.name,
    value: item.value,
    payload: item.payload || {},
    itemStyle: {
      color: BAR_CHART_COLORS[index % BAR_CHART_COLORS.length],
    },
    label: {
      show: showLabel !== false,
      position: 'top',
      color: '#444',
      distance: 4,
      fontSize: 12,
      formatter: (params: any) => formatter(params.value),
    },
  }));
};

/**
 * 生成高亮 markArea 配置
 * @param data 图表数据
 * @param highlightName 高亮的数据项名称
 * @returns markArea 配置
 */
const getBarChartMarkArea = (data: IBarChartData[], highlightName?: string): any => {
  if (!highlightName) {
    return undefined;
  }

  const highlightIndex = data.findIndex((item) => item.name === highlightName);
  if (highlightIndex === -1) {
    return undefined;
  }

  return {
    itemStyle: {
      color: HIGHLIGHT_BG_COLOR,
    },
    data: [[{ xAxis: highlightIndex }, { xAxis: highlightIndex }]],
  };
};

/**
 * 生成 Y 轴配置
 * @param yAxisLabelFormatter Y轴标签格式化函数
 * @returns Y轴配置
 */
const getBarChartYAxisConfig = (yAxisLabelFormatter?: TValueFormatter | null): any => {
  const formatter = yAxisLabelFormatter || formatKMBWithPlaceholder;

  return {
    type: 'value',
    axisLabel: {
      color: '#838697',
      fontSize: 12,
      formatter: (value: number) => formatter(value),
    },
    splitLine: {
      lineStyle: {
        type: 'dashed',
        color: '#E5E5E5',
      },
    },
    show: true,
  };
};

/**
 * 生成 Tooltip 配置
 * @param data 图表数据
 * @param metricName 指标名称
 * @param valueFormatter 数值格式化函数
 * @param nameFormatter 名称格式化函数
 * @param customTooltipFormatter 自定义 Tooltip 格式化函数
 * @returns Tooltip 配置
 */
const getBarChartTooltipConfig = (
  data: IBarChartData[],
  metricName?: string,
  valueFormatter?: TValueFormatter | null,
  nameFormatter?: TNameFormatter | null,
  customTooltipFormatter?: ((params: any) => string) | null,
): any => {
  const valFormatter = valueFormatter || formatKMBWithPlaceholder;

  // 如果提供了自定义 Tooltip 格式化函数，直接使用
  if (customTooltipFormatter) {
    return {
      trigger: 'axis',
      padding: 0,
      borderWidth: 0,
      backgroundColor: 'rgba(255, 255, 255, 0)',
      confine: true,
      hideDelay: 200,
      triggerOn: 'mousemove',
      axisPointer: {
        type: 'shadow',
      },
      formatter: customTooltipFormatter,
    };
  }

  return {
    trigger: 'axis',
    padding: 0,
    borderWidth: 0,
    backgroundColor: 'rgba(255, 255, 255, 0)',
    confine: true,
    hideDelay: 200,
    triggerOn: 'mousemove',
    axisPointer: {
      type: 'shadow',
    },
    formatter: (params: any[]) => {
      if (!params || params.length === 0) {
        return '';
      }

      const firstParam = params[0];

      const dataList = params.map((param) => {
        const displayName = nameFormatter
          ? nameFormatter(metricName || param.seriesName || '')
          : metricName || param.seriesName || '';

        return {
          color: param.color || BAR_CHART_COLORS[param.dataIndex % BAR_CHART_COLORS.length],
          name: displayName,
          value: valFormatter(param.value),
          payload: param.data?.payload || {},
        };
      });

      return renderBarChartTooltip({
        title: firstParam.name,
        metricName,
        dataList,
      });
    },
  };
};

/**
 * 生成 X 轴标签格式化函数
 * @param data 图表数据
 * @param highlightName 高亮的数据项名称
 * @param nameFormatter 名称formatter
 * @returns X轴标签格式化函数
 */
const getBarChartXAxisLabelFormatter = (
  data: IBarChartData[],
  highlightName?: string,
  nameFormatter?: ((value: string) => string) | null,
): ((value: string) => string) => {
  if (typeof nameFormatter === 'function') {
    return nameFormatter;
  }
  return (value: string): string => {
    const isHighlight = highlightName && value === highlightName;
    const nameStyle = isHighlight ? 'highlightName' : 'normalName';
    return `{${nameStyle}|${value}}`;
  };
};

/**
 * 获取X轴基础 rich 配置（名称样式）
 * @param highlightName 高亮的数据项名称
 * @returns X轴 rich 配置
 */
const getBarChartXAxisRich = (highlightName?: string): Record<string, any> => {
  const baseRich: Record<string, any> = {
    normalName: {
      color: '#000',
      opacity: 0.45,
      fontSize: 12,
      align: 'center',
    },
  };

  if (highlightName) {
    baseRich.highlightName = {
      color: '#262832',
      fontSize: 12,
      fontWeight: 'bold',
      align: 'center',
    };
  }

  return baseRich;
};

/**
 * 获取柱状图动态配置
 * @param params 配置参数
 * @returns ECharts option 对象
 */
export const getBarChartDynamicOption = (params: IGetBarChartDynamicOptionParams): any => {
  const {
    data,
    metricName,
    highlightName,
    showLabel = true,
    useXAxisLabelRotate,
    xAxisLabelPadding,
    yAxisLabelFormatter,
    valueLabelFormatter,
    nameFormatter,
    tooltipFormatter,
  } = params;

  if (!data || data.length === 0) {
    return {
      xAxis: { data: [] },
      yAxis: [getBarChartYAxisConfig(yAxisLabelFormatter)],
      series: [],
    };
  }

  // 生成 series 数据
  const seriesData = getBarChartSeriesData(data, showLabel, valueLabelFormatter);

  // 生成 markArea（高亮）
  const markArea = getBarChartMarkArea(data, highlightName);

  // 生成 X 轴 rich 配置
  const xAxisRich = getBarChartXAxisRich(highlightName);

  // 生成 X 轴标签格式化函数
  const xAxisLabelFormatter = getBarChartXAxisLabelFormatter(data, highlightName, nameFormatter);

  return {
    grid: showLabel ? BAR_GRID_WITH_LABEL : BAR_GRID_WITHOUT_LABEL,
    tooltip: getBarChartTooltipConfig(data, metricName, valueLabelFormatter, nameFormatter, tooltipFormatter),
    xAxis: {
      type: 'category',
      axisTick: {
        alignWithLabel: !highlightName,
        lineStyle: {
          color: '#BFBFBF',
          width: 1,
        },
      },
      axisLine: {
        show: true,
        lineStyle: {
          color: '#BFBFBF',
          width: 1,
        },
      },
      boundaryGap: true,
      axisLabel: {
        padding: xAxisLabelPadding,
        color: '#000',
        align: 'center',
        opacity: 0.45,
        fontSize: 12,
        margin: 8,
        rotate: useXAxisLabelRotate ? 45 : 0,
        formatter: xAxisLabelFormatter,
        rich: xAxisRich,
      },
      data: data.map((item) => item.name),
    },
    yAxis: [getBarChartYAxisConfig(yAxisLabelFormatter)],
    series: [
      {
        type: 'bar',
        barWidth: BAR_WIDTH,
        barMaxWidth: BAR_MAX_WIDTH,
        barMinWidth: BAR_MIN_WIDTH,
        data: seriesData,
        markArea,
      },
    ],
  };
};
